import {cn, Popover, PopoverContent, PopoverTrigger, Spinner} from "@heroui/react";
import {Icon} from "@iconify/react";
import {Command} from "cmdk";
import React, {useState} from "react";
import {apiRetrieveImproved} from "../api.ts";
import PButton from "./PButton.tsx";
import PInput from "./PInput.tsx";

export interface GooglePlace {
    place_id: string;
    description: string;
    structured_formatting: {
        main_text: string;
        secondary_text: string;
    };
}

export interface PlaceDetails {
    name: string;
    formatted_address: string;
    place_id: string;
    geometry: {
        location: {
            lat: number;
            lng: number;
        };
    };
    address_components?: Array<{
        long_name: string;
        short_name: string;
        types: string[];
    }>;
}

interface PPlacesAutocompleteProps {
    label?: string;
    placeholder?: string;
    defaultValue?: string;
    countryCode?: string;
    types?: string; // e.g., 'establishment', 'geocode', 'address'
    onPlaceSelect?: (place: PlaceDetails | null) => void;
    isDisabled?: boolean;
    className?: string;
}

export default function PPlacesAutocomplete({
    label = "Search location",
    placeholder = "Search for a place or address...",
    defaultValue = "",
    countryCode,
    types,
    onPlaceSelect,
    isDisabled = false,
    className = "",
}: PPlacesAutocompleteProps) {
    const [inputValue, setInputValue] = useState(defaultValue);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [places, setPlaces] = useState<GooglePlace[]>([]);
    const [searchPerformed, setSearchPerformed] = useState(false);

    const handleSearch = async () => {
        if (!inputValue || inputValue.length < 2) {
            setPlaces([]);
            setSearchPerformed(true);
            return;
        }

        setIsLoading(true);
        setSearchPerformed(true);

        const params: Record<string, string> = {input: inputValue};
        if (countryCode) {
            params.country = countryCode;
        }
        if (types) {
            params.types = types;
        }

        try {
            const json = await apiRetrieveImproved('places-autocomplete' as any, params);

            if (json.error) {
                console.error('Places API error:', json.error);
                setPlaces([]);
            } else {
                setPlaces(json.predictions || []);
            }
        } catch (error) {
            console.error('Places API error:', error);
            setPlaces([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePlaceSelect = async (place: GooglePlace) => {
        setInputValue(place.description);
        setIsOpen(false);

        if (!onPlaceSelect) {
            return;
        }

        try {
            const json = await apiRetrieveImproved('place-details' as any, {place_id: place.place_id});

            if (json.error || json.status !== 'OK') {
                console.error('Place details error:', json.error || json.status);
                onPlaceSelect(null);
                return;
            }

            onPlaceSelect(json.result);
        } catch (error) {
            console.error('Failed to fetch place details:', error);
            onPlaceSelect(null);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSearch();
        }
    };

    return (
        <div className={cn(className)}>
            <PInput
                isDisabled={isDisabled}
                type="text"
                label={label}
                labelPlacement="outside"
                placeholder={placeholder}
                value={inputValue}
                onValueChange={setInputValue}
                onKeyDown={handleKeyDown}
                endContent={
                    <div className="flex items-center gap-1">
                        <Popover
                            placement="bottom-end"
                            shouldFlip={false}
                            shouldCloseOnScroll={false}
                            isOpen={isOpen}
                            onOpenChange={(open) => setIsOpen(open)}
                        >
                            <PopoverTrigger>
                                <PButton
                                    size="sm"
                                    color="primary"
                                    variant="bordered"
                                    isLoading={isLoading}
                                    onPress={() => {
                                        handleSearch();
                                        setIsOpen(true);
                                    }}
                                >
                                    <Icon icon="material-symbols-light:search" className="text-lg" />
                                    Search
                                </PButton>
                            </PopoverTrigger>
                            <PopoverContent className="w-80">
                                <Command>
                                    <Command.List className="max-h-64 overflow-y-auto">
                                        {isLoading && (
                                            <div className="flex items-center justify-center p-4">
                                                <Spinner size="sm" />
                                            </div>
                                        )}

                                        {!isLoading && searchPerformed && places.length === 0 && (
                                            <Command.Empty className="p-4 text-center text-default-400">
                                                {inputValue.length < 2
                                                    ? "Enter at least 2 characters"
                                                    : "No places found"}
                                            </Command.Empty>
                                        )}

                                        {!isLoading && places.map((place) => (
                                            <Command.Item
                                                key={place.place_id}
                                                value={place.place_id}
                                                onSelect={() => handlePlaceSelect(place)}
                                                className="cursor-pointer p-2 hover:bg-default-100 rounded-md"
                                            >
                                                <div className="flex flex-col">
                                                    <span className="text-small font-medium">
                                                        {place.structured_formatting?.main_text || place.description}
                                                    </span>
                                                    {place.structured_formatting?.secondary_text && (
                                                        <span className="text-tiny text-default-400">
                                                            {place.structured_formatting.secondary_text}
                                                        </span>
                                                    )}
                                                </div>
                                            </Command.Item>
                                        ))}
                                    </Command.List>
                                </Command>
                            </PopoverContent>
                        </Popover>
                    </div>
                }
            />
        </div>
    );
}
